'use client';
import { useContext, useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';

import { SessionContext } from '@benzinga/session-context';
import { useWatchlistContext } from '@benzinga/user-context';
import { Watchlist, WatchlistManager } from '@benzinga/watchlist-manager';
import { Modal, Spinner } from '@benzinga/core-ui';
import { PortfolioModalHeader } from './PortfolioModalHeader';
import {
  portfolioModalBodyClass,
  portfolioModalContainerStyle,
  portfolioModalFooterClass,
  portfolioModalShellClass,
} from './modalStyles';

export const DeleteTicker = () => {
  const { deleteTickerModal, setDeleteTickerModal, watchlists } = useWatchlistContext();
  const session = useContext(SessionContext);
  const watchlistManager = session.getManager(WatchlistManager);
  const [currentWatchlist, setCurrentWatchlist] = useState<Watchlist | null>(null);
  const [tickerWatchlists, setTickerWatchlists] = useState<Watchlist[]>([]);

  const handleDeleteTicker = async (watchlist: Watchlist | null, ticker: string | null) => {
    const watchlistTicker = currentWatchlist?.symbols.find(symbol => symbol.symbol === ticker);
    if (!ticker || !watchlist || !watchlistTicker) return;

    setDeleteTickerModal({ show: true, ticker, isLoading: true });
    try {
      const result = await watchlistManager.removeTickersFromWatchlist(watchlist, [watchlistTicker]);
      if (result.err) {
        toast.error('Unable to remove ticker. Please try again.');
        setDeleteTickerModal({ show: true, ticker, isLoading: false });
        return;
      }
      toast.success(`${ticker} removed from ${watchlist.name} watchlist`);
      setDeleteTickerModal({ show: false, ticker: null, isLoading: false });
    } catch (error) {
      console.error('Error deleting ticker from watchlist:', error);
      toast.error('Error deleting ticker from watchlist. Please try again.');
      setDeleteTickerModal({ show: true, ticker, isLoading: false });
    }
  };

  const cancelDeleteTicker = () => {
    setDeleteTickerModal({ show: false, ticker: null, isLoading: false });
  };

  useEffect(() => {
    if (deleteTickerModal.ticker) {
      const matchingWatchlists = watchlists.filter(watchlist =>
        watchlist.symbols.some(symbol => symbol.symbol === deleteTickerModal.ticker),
      );
      setTickerWatchlists(matchingWatchlists);
      setCurrentWatchlist(matchingWatchlists[0]);
    }
  }, [deleteTickerModal.ticker, watchlists]);

  return (
    <Modal
      visible={deleteTickerModal.show}
      onClose={cancelDeleteTicker}
      disableFullScreenButton={true}
      hideTitle={true}
      fullScreen={true}
      size={{ manual: { width: '420px', maxWidth: 'calc(100vw - 32px)', height: 'auto' } }}
      containerStyle={portfolioModalContainerStyle}
    >
      <div className={portfolioModalShellClass}>
        <PortfolioModalHeader title="Remove ticker" onClose={cancelDeleteTicker} />
        <div className={portfolioModalBodyClass}>
          <p className="text-sm leading-6 text-slate-600">
            Remove <strong className="text-slate-950">{deleteTickerModal.ticker}</strong> from a watchlist.
          </p>
          {tickerWatchlists.length > 1 && (
            <select
              className="mt-4 min-h-10 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-900 focus:border-bzblue-500 focus:outline-none focus:ring-2 focus:ring-bzblue-100"
              value={currentWatchlist?.watchlistId ?? ''}
              disabled={deleteTickerModal.isLoading}
              onChange={event =>
                setCurrentWatchlist(
                  tickerWatchlists.find(watchlist => watchlist.watchlistId === event.target.value) ?? null,
                )
              }
            >
              {tickerWatchlists.map(watchlist => (
                <option key={watchlist.watchlistId} value={watchlist.watchlistId}>
                  {watchlist.name}
                </option>
              ))}
            </select>
          )}
          {tickerWatchlists.length === 1 && (
            <p className="mt-3 text-sm font-semibold text-slate-900">{tickerWatchlists[0].name}</p>
          )}
        </div>
        <div className={portfolioModalFooterClass}>
          <button
            type="button"
            onClick={cancelDeleteTicker}
            disabled={deleteTickerModal.isLoading}
            className="min-h-10 flex-1 rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => handleDeleteTicker(currentWatchlist, deleteTickerModal.ticker)}
            disabled={deleteTickerModal.isLoading}
            className="inline-flex h-10 flex-1 items-center justify-center gap-2 rounded-md bg-red-600 px-4 text-sm font-bold text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {deleteTickerModal.isLoading ? <Spinner size={16} /> : 'Remove'}
          </button>
        </div>
      </div>
    </Modal>
  );
};
